import React from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { DevFooter } from 'rental/chrome/DevFooter';
import { rentalUrqlOptions } from 'rental/urql';
import { AppLayout } from 'shared/app/AppLayout';
import { AppWrapper } from 'shared/app/AppWrapper';
import { DangerouslySetHtmlContent } from 'shared/components/DangerousDiv';
import { ErrorBoundary } from 'shared/components/ErrorBoundary';
import { PhoneNumberLink } from 'shared/components/PhoneNumber';
import { AppExpiredInfo } from 'shared/urql';
import { AppExpiredModal } from '../chrome/AppExpiredModal';
import { PortalBanner } from '../chrome/PortalBanner';
import { PortalStylesheet } from '../chrome/PortalStylesheet';
import { Finished } from '../steps/finished/Finished';
import { Paid } from '../steps/payment/Paid';
import { AppContent } from './AppContent';
import { AppContext, isAppContextData, isAppLoading, isGraphQLError, isNetworkError, isOtherError, isPortalContextData, isPortalDoesNotExistError, useAppContextProvider } from './AppContext';
import { EmptyApplication } from './EmptyApplication';
import { slugs, rentalSteps, pathPattern } from './steps';


export const App = () => <AppWrapper urql={rentalUrqlOptions}>{AppInner}</AppWrapper>;


export interface Props {
  expiryInfo?: AppExpiredInfo
}


export const AppInner = ({ expiryInfo }: Props) => { 
  const app = useAppContextProvider();
  const menu = rentalSteps.useMenu(
    isPortalContextData(app) && (app.application && app.portal) ? {application: app.application, portal: app.portal} : null,
    pathPattern,
  );
  
  if (isPortalContextData(app)) {
    const { application, portal } = app;
    const { search: queryString } = document.location;
    return (
      <AppContext.Provider value={app}>
        <PortalStylesheet themeSlug={portal.themeSlug} />

        <AppLayout 
          footer={{
            tradingName: portal.tradingName,
            privacyUrl: portal.policies.privacyPolicyUrl,
            termsOfUseUrl: portal.policies.websiteTermsUrl,
          }}
          devFooter={(process.env.REACT_APP_SHOW_DEV_FOOTER === 'true' || application?.testMode.application) && <DevFooter/>}
        >

          <Switch>
            {/* Redirect to the cart page if no slug specified */}
            {application && 
              <Route path={app.buildLink()} exact={true}>
                {application.inStore
                  ? <Redirect to={{
                    pathname: app.buildLink(slugs.welcome),
                    search: queryString,
                  }} />
                  : <Redirect to={{
                    pathname: app.buildLink(slugs.cart),
                    search: queryString,
                  }} />
                }
              </Route>
            }

            {/* Handle post-direct-post landing page */}
            <Route path={app.buildLink(slugs.paid)}>
              {application && <Paid application={application} portal={portal} />}
            </Route>

            {/* Allow finished step if previous application completed */}
            {(!application || application?.dateSubmitted) && portal?.myLastApplicationId && (
              <Route path={app.buildLink(slugs.finished)}>
                <PortalBanner portal={portal} application={application} menu={menu} />
                <Finished portal={portal} application={application} />
              </Route>
            )}

            {/* Otherwise, show the app steps */}
            <Route>
              
              {/* The Welcome step does not show the header, so check if we are on that step */}
              <Route exact path={app.buildLink(slugs.welcome)}>
                {({match: hideBanner}) => 
                  <>
                    {!hideBanner && 
                      <PortalBanner portal={portal} application={application} menu={menu} />
                    }
                    
                    <ErrorBoundary title="App.Content">
                      <div className="pb-5 mx-1 mx-sm-3">
                        {isAppContextData(app) && !app.application.dateSubmitted ? <AppContent {...app}/> : <EmptyApplication portal={portal}/>}
                      </div>
                    </ErrorBoundary>

                  </>
                }
              </Route>

            </Route>
          
          </Switch>

        </AppLayout>

        {/* Additional scripts provided by each portal for analytics/tracking */}
        <DangerouslySetHtmlContent className="d-none" html={application?.remarketingScript ?? ''} />

        <AppExpiredModal info={expiryInfo} />
        
      </AppContext.Provider>
    )
  } else if (isAppLoading(app)) {
    return <div className="text-center">Loading...</div>;
  } else if (isNetworkError(app)) {
    return (
      <div className="text-center">
        <b>It looks like there was a Network Error.</b> <br/> 
        Reloading the page often fixes this.<br/><br/>
        <ReloadPageButton/><br/><br/><br/>
        If this continues to be a problem,<br/>please contact us on <PhoneNumberLink/>
      </div>
    );
  } else if (isGraphQLError(app) || isOtherError(app)) {
    return (
      <div className="text-center">
        <b>Something has gone wrong</b> <br/> 
        Reloading the page may fix this.<br/><br/>
        <ReloadPageButton/><br/><br/><br/>
        If you still have an issue after reloading,<br/>please contact us on <PhoneNumberLink/>
      </div>
    );
  } else if (isPortalDoesNotExistError(app)) {
    return (
        <div className="text-center">
          <p>Portal does not exist: <code>"{app.portalSlug}"</code></p>
          <p>Ensure your URL contains a valid portal slug such as <code>/musicorp/</code></p>
        </div>
    );
  }

  return <div>Unknown error when loading application context...</div>;
}


const ReloadPageButton = () => (
  <button className="btn btn-outline-dark mx-2" onClick={() => window.location.reload()}>
    Reload the page
  </button>
);
